import React, { useState, useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * useConfirmDialog — reemplazo brandeado de `window.confirm()`.
 *
 * Uso:
 *   const { confirm, ConfirmDialog } = useConfirmDialog();
 *
 *   // En algun handler:
 *   const ok = await confirm({
 *     title: "Eliminar cliente",
 *     description: "Esta accion no se puede deshacer.",
 *     confirmText: "Eliminar",
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *   // ...accion destructiva...
 *
 * Render:
 *   return (<>
 *     ...
 *     <ConfirmDialog />
 *   </>);
 *
 * Ventajas sobre window.confirm():
 * - Estilo brandeado (igual que el resto de la UI, shadcn AlertDialog).
 * - Permite texto multilinea y destacar acciones destructivas en rojo.
 * - No bloquea el hilo principal: devuelve una Promise<boolean>.
 */
export function useConfirmDialog() {
  const [state, setState] = useState({
    open: false,
    title: '',
    description: '',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    destructive: false,
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: options.title || 'Confirmar',
        description: options.description || '',
        confirmText: options.confirmText || 'Confirmar',
        cancelText: options.cancelText || 'Cancelar',
        destructive: !!options.destructive,
      });
    });
  }, []);

  const handleCancel = () => {
    setState((s) => ({ ...s, open: false }));
    resolverRef.current?.(false);
    resolverRef.current = null;
  };

  const handleConfirm = () => {
    setState((s) => ({ ...s, open: false }));
    resolverRef.current?.(true);
    resolverRef.current = null;
  };

  const ConfirmDialog = useCallback(
    () => (
      <AlertDialog open={state.open} onOpenChange={(o) => !o && handleCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription className="whitespace-pre-line">
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>{state.cancelText}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={state.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : undefined}
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ),
    [state]
  );

  return { confirm, ConfirmDialog };
}
